import { useEffect, useState } from "react";
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  Spinner,
  useDisclosure,
} from "@heroui/react";
import { api } from "@/api";
import type { OfficeEstimate, EstimateHistory } from "@/api/analytics";
import { EstimateChart } from "./estimateChart";

interface Props {
  disclosure: ReturnType<typeof useDisclosure>;
  officeId: number | null;
  officeName?: string;
  areaSqm: number;
  locationId: number;
}

const fmt = (v: number | null) =>
  v == null
    ? "нет данных"
    : new Intl.NumberFormat("ru-RU", {
        style: "currency",
        currency: "RUB",
        maximumFractionDigits: 0,
      }).format(v);

export function EstimateModal({
  disclosure,
  officeId,
  officeName,
  areaSqm,
  locationId,
}: Props) {
  const { isOpen, onOpenChange } = disclosure;
  const [estimate, setEstimate] = useState<OfficeEstimate | null>(null);
  const [history, setHistory] = useState<EstimateHistory | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen || !officeId) return;
    setLoading(true);
    setError(null);

    const now = new Date();
    const from = new Date(now.getFullYear() - 1, now.getMonth(), 1)
      .toISOString()
      .slice(0, 10);
    const to = now.toISOString().slice(0, 10);

    Promise.all([
      api.analytics.getEstimate(officeId),
      api.analytics.getHistory(locationId, from, to, officeId),
    ])
      .then(([est, hist]) => {
        setEstimate(est.estimates[0] ?? null);
        setHistory(hist);
      })
      .catch((err) => setError(err?.message || "Ошибка загрузки"))
      .finally(() => setLoading(false));
  }, [isOpen, officeId, locationId]);

  return (
    <Modal
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      size="2xl"
      scrollBehavior="inside"
    >
      <ModalContent>
        <ModalHeader className="flex items-center gap-2">
          📈 Оценка аренды{officeName ? ` — ${officeName}` : ""}
        </ModalHeader>
        <ModalBody className="pb-6">
          {loading && <Spinner className="mx-auto my-8" />}
          {error && <p className="text-danger text-center my-4">{error}</p>}
          {!loading && !error && (
            <>
              {estimate ? (
                <div className="grid grid-cols-3 gap-3 mb-4">
                  <div className="bg-default-100 rounded-lg p-3 text-center">
                    <div className="text-xs text-foreground-500">Нижняя</div>
                    <div className="text-lg font-semibold">
                      {fmt(estimate.estimate_low_monthly_rur)}
                    </div>
                  </div>
                  <div className="bg-blue-100 dark:bg-blue-900/30 rounded-lg p-3 text-center">
                    <div className="text-xs text-foreground-500">Оценка</div>
                    <div className="text-lg font-semibold text-blue-600">
                      {fmt(estimate.estimate_monthly_rur)}
                    </div>
                  </div>
                  <div className="bg-default-100 rounded-lg p-3 text-center">
                    <div className="text-xs text-foreground-500">Верхняя</div>
                    <div className="text-lg font-semibold">
                      {fmt(estimate.estimate_high_monthly_rur)}
                    </div>
                  </div>
                </div>
              ) : (
                <p className="text-foreground-400 text-center my-4">
                  Нет сопоставимого рынка для этого офиса
                </p>
              )}
              {history &&
                history.steps.length > 0 &&
                (estimate?.area_sqm ?? areaSqm) > 0 && (
                  <EstimateChart
                    steps={history.steps}
                    areaSqm={estimate?.area_sqm ?? areaSqm}
                    estimateRub={
                      estimate
                        ? {
                            low: estimate.estimate_low_monthly_rur,
                            median: estimate.estimate_monthly_rur,
                            high: estimate.estimate_high_monthly_rur,
                          }
                        : undefined
                    }
                  />
                )}
              {estimate && (
                <p className="text-xs text-foreground-400 text-center mt-2">
                  Обновлено:{" "}
                  {new Date(estimate.computed_at).toLocaleDateString("ru-RU")}
                </p>
              )}
            </>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}
